// Estrutura de extremos do Cripto Alert — parte do motor Trend Structure, calculada a cada candle.
//
// Regras (Rodrigo, 2026-11-05):
// - Em cada tendência existe só UM de cada: HH, HL, LH e LL.
// - HH e LL são sempre os extremos (pavio): o topo máximo e o fundo mínimo da tendência.
// - Alta:  LL = fundo mínimo de onde a alta nasceu | HH = topo máximo | HL = fundo defendido pelo motor
//          | LH = o topo mais alto depois do HH, contando só depois do fundo do recuo (sempre abaixo do HH).
// - Baixa: HH = topo de onde a baixa nasceu | LL = fundo mínimo | LH = topo defendido pelo motor
//          | HL = o TOPO mais alto depois do LL (o topo do repique; marca acima do candle).
//          Escolha do Rodrigo em 2026-11-05 (BTC 4h): não é o espelho geométrico do LH da alta.
// - LH (alta) e HL (baixa) são topos e seguem a mesma regra (Rodrigo, 2026-11-05):
//   só aparecem quando um candle FECHA acima da máxima do candle anterior (regra de candidato do Vela) e só
//   se atualizam nesses fechamentos, indo para a máxima mais alta até ali. Quando um candle fecha abaixo da
//   mínima do anterior, o topo nasce: vira candidato, na máxima da subida. No próximo fechamento acima da
//   máxima do anterior sem máxima nova, vira confirmado.
// - Estágios (regra do Vela): formação -> candidato (topo: um candle fecha abaixo da mínima do anterior;
//   fundo: fecha acima da máxima do anterior) -> confirmado (nasce o candidato do lado oposto).
// - Pavio que passa do extremo sem fechar além dele também move o extremo; fica marcado via 'pavio'.
//
// IMPORTANTE: esta camada só LÊ o estado do motor. Nada daqui é usado para decidir a tendência,
// o ChoCH ou a virada — esses continuam exatamente os do motor do Vela.

export const CA_STRUCTURE_RULE = 'ca-extremes-v6.2026-11-05';

const HIGH_TYPES = new Set(['H', 'HH', 'LH']);
const LOW_TYPES = new Set(['L', 'HL', 'LL']);

function round(value) {
  return value === null || value === undefined || !Number.isFinite(value) ? null : Number(value.toFixed(12));
}

function mark(type, candle, field, stage, role, extra = {}) {
  return { type, price: round(candle[field]), at: candle.startAt, field, stage, role, ...extra };
}

function clonePoint(point) {
  return point ? { ...point } : null;
}

export function cloneCaStructureState(machine) {
  return machine
    ? {
        rule: machine.rule,
        trend: machine.trend,
        origin: clonePoint(machine.origin),
        extreme: clonePoint(machine.extreme),
        counter: clonePoint(machine.counter),
        run: clonePoint(machine.run),
        pull: clonePoint(machine.pull),
        approximate: Boolean(machine.approximate),
      }
    : null;
}

// Avança o estágio de um ponto com a regra de candidato do Vela.
function advanceStage(point, prev, cur, side) {
  if (!point || !prev || point.at === cur.startAt || point.stage === 'confirmado') return;
  const away = side === 'top' ? cur.close < prev.low : cur.close > prev.high;
  const back = side === 'top' ? cur.close > prev.high : cur.close < prev.low;
  if (point.stage === 'formacao' && away) point.stage = 'candidato';
  else if (point.stage === 'candidato' && back) point.stage = 'confirmado';
}

function labels(bull) {
  return bull
    ? { origin: 'LL', extreme: 'HH', counter: 'LH', exField: 'high', orField: 'low', exSide: 'top', orSide: 'bottom' }
    : { origin: 'HH', extreme: 'LL', counter: 'HL', exField: 'low', orField: 'high', exSide: 'bottom', orSide: 'top' };
}

// Um candle dentro da tendência atual.
function stepMachine(m, prev, cur) {
  const bull = m.trend === 'bullish';
  const L = labels(bull);
  const beyond = (a, b) => (bull ? a > b : a < b); // além, no sentido da tendência
  const against = (a, b) => (bull ? a < b : a > b); // além, no sentido contrário

  // Origem: o extremo oposto da tendência. Se o preço passar dela sem a tendência virar, ela acompanha.
  if (against(round(cur[L.orField]), m.origin.price)) {
    m.origin = mark(L.origin, cur, L.orField, 'formacao', 'origem');
  } else {
    advanceStage(m.origin, prev, cur, L.orSide);
  }

  // Extremo (HH na alta / LL na baixa): sempre o topo máximo / fundo mínimo.
  if (!m.extreme || beyond(round(cur[L.exField]), m.extreme.price)) {
    const via = !m.extreme ? null : beyond(cur.close, m.extreme.price) ? 'fechamento' : 'pavio';
    m.extreme = mark(L.extreme, cur, L.exField, 'formacao', 'extremo', { via });
    m.counter = null;
    m.run = null;
    m.pull = null;
    return;
  }
  advanceStage(m.extreme, prev, cur, L.exSide);
  stepCounterTop(m, prev, cur, bull);
}

// LH (alta) / HL (baixa): o topo mais alto do movimento contra, mostrado só nos fechamentos acima da máxima
// do candle anterior. Baixa: conta desde o LL. Alta: conta desde o fundo do recuo (o menor fundo depois do HH).
function stepCounterTop(m, prev, cur, bull) {
  const high = round(cur.high);
  const closedAbove = Boolean(prev) && cur.close > prev.high; // subida: fechou acima da máxima do anterior
  const closedBelow = Boolean(prev) && cur.close < prev.low; // nasceu o topo: fechou abaixo da mínima do anterior
  if (bull) {
    const low = round(cur.low);
    if (!m.pull || low < m.pull.price) {
      // fundo do recuo mais baixo: os topos contam a partir dele
      m.pull = { price: low, at: cur.startAt };
      m.run = closedAbove ? { price: high, at: cur.startAt } : null;
    } else if (!m.run || high > m.run.price) {
      m.run = { price: high, at: cur.startAt };
    }
  } else if (!m.run || high > m.run.price) {
    m.run = { price: high, at: cur.startAt };
  }
  const run = m.run;
  const inside = run && (bull ? run.price < m.extreme.price : run.price > m.extreme.price);
  const higher = inside && (!m.counter || run.price > m.counter.price);
  const type = bull ? 'LH' : 'HL';
  if (closedAbove) {
    if (higher) m.counter = { type, price: run.price, at: run.at, field: 'high', stage: 'formacao', role: 'contra' };
    else if (m.counter?.stage === 'candidato') m.counter.stage = 'confirmado';
  } else if (closedBelow && m.counter) {
    // o topo nasceu: o candidato é a máxima mais alta da subida
    if (higher) m.counter = { type, price: run.price, at: run.at, field: 'high', stage: 'candidato', role: 'contra' };
    else if (m.counter.stage === 'formacao') m.counter.stage = 'candidato';
  }
}

function indexOfCandle(candles, at, toIndex) {
  for (let i = toIndex; i >= 0; i -= 1) if (candles[i].startAt === at) return i;
  return -1;
}

// Monta a tendência a partir da origem, repassando os candles da origem até o candle atual.
function machineFromOrigin(trend, origin, candles, originIndex, toIndex) {
  const m = { rule: CA_STRUCTURE_RULE, trend, origin, extreme: null, counter: null, run: null, pull: null, approximate: false };
  for (let i = originIndex + 1; i <= toIndex; i += 1) stepMachine(m, candles[i - 1], candles[i]);
  return m;
}

// Primeira tendência da janela: origem = extremo oposto de toda a janela até aqui.
function bootstrap(trend, candles, toIndex) {
  const bull = trend === 'bullish';
  const L = labels(bull);
  let best = 0;
  for (let i = 1; i <= toIndex; i += 1) {
    const better = bull ? candles[i].low < candles[best].low : candles[i].high > candles[best].high;
    if (better) best = i;
  }
  const origin = mark(L.origin, candles[best], L.orField, 'confirmado', 'origem');
  return machineFromOrigin(trend, origin, candles, best, toIndex);
}

// Virada: o extremo da tendência que acabou vira a origem da nova.
function reverse(previous, trend, candles, toIndex) {
  const L = labels(trend === 'bullish');
  const source = previous.extreme ?? previous.origin;
  const origin = { ...source, type: L.origin, role: 'origem', stage: 'confirmado' };
  delete origin.via;
  const originIndex = indexOfCandle(candles, origin.at, toIndex);
  if (originIndex < 0) {
    // A origem saiu da janela de candles (só no modo incremental): segue a partir do candle atual.
    const m = { rule: CA_STRUCTURE_RULE, trend, origin, extreme: null, counter: null, run: null, pull: null, approximate: true };
    stepMachine(m, candles[toIndex - 1] ?? null, candles[toIndex]);
    return m;
  }
  return machineFromOrigin(trend, origin, candles, originIndex, toIndex);
}

// Chamado pelo motor depois de processar o candle candles[index], com a tendência já decidida pelo motor.
export function advanceCaStructure(machine, trend, candles, index) {
  if (trend !== 'bullish' && trend !== 'bearish') return null;
  if (!machine || !machine.origin) return bootstrap(trend, candles, index);
  if (machine.trend !== trend) return reverse(machine, trend, candles, index);
  stepMachine(machine, candles[index - 1] ?? null, candles[index]);
  return machine;
}

function samePoint(a, b) {
  return Boolean(a && b && a.at === b.at && a.field === b.field);
}

// As 4 marcas da tendência atual (uma de cada). `defended` é o fundo (alta) / topo (baixa) que o motor defende.
export function buildCaStructure(machine, motorState) {
  if (!machine || (machine.trend !== 'bullish' && machine.trend !== 'bearish')) return null;
  const bull = machine.trend === 'bullish';
  const motorDefended = bull ? motorState?.officialBottom : motorState?.officialTop;
  const defendedOk = motorDefended && (bull ? LOW_TYPES : HIGH_TYPES).has(motorDefended.type);
  const origin = clonePoint(machine.origin);
  const extreme = clonePoint(machine.extreme);
  const counter = clonePoint(machine.counter);
  let defended = defendedOk
    ? {
        type: bull ? 'HL' : 'LH',
        price: round(motorDefended.price),
        at: motorDefended.at,
        field: bull ? 'low' : 'high',
        stage: motorDefended.stage === 'final' ? 'confirmado' : 'candidato',
        role: 'defendido',
      }
    : null;
  // Mesmo candle e mesmo pavio da origem: fica uma marca só (a origem), avisando que ela é a defendida.
  if (defended && samePoint(defended, origin)) {
    origin.defended = true;
    defended = null;
  }
  // Mesmo candle e mesmo pavio do contra (na baixa, HL, LH e HH são topos): fica o defendido ou a origem.
  // Ex.: na baixa, se depois do LL o preço passou do HH de origem sem a tendência virar, o topo mais alto
  // depois do LL é o próprio HH — fica só o HH.
  const counterOut = counter && (samePoint(counter, defended) || samePoint(counter, origin)) ? null : counter;
  const out = {
    rule: CA_STRUCTURE_RULE,
    trend: machine.trend,
    approximate: Boolean(machine.approximate),
    hh: bull ? extreme : origin,
    hl: bull ? defended : counterOut,
    lh: bull ? counterOut : defended,
    ll: bull ? origin : extreme,
  };
  return out;
}

// Todos os pontos para o gráfico: as 4 marcas e, em cinza, os topos/fundos internos do motor (sem letra).
export function caStructurePoints(snapshot) {
  const structure = snapshot?.structure;
  const ledger = Array.isArray(snapshot?.pivotLedger) ? snapshot.pivotLedger : [];
  const marks = structure ? [structure.ll, structure.hh, structure.hl, structure.lh].filter(Boolean) : [];
  const out = marks.map((p) => ({ ...p, label: p.type, side: p.field === 'high' ? 'top' : 'bottom' }));
  const taken = new Set(out.map((p) => `${p.side}|${p.at}`));
  for (const p of ledger) {
    if (!p?.at || p.price === null || p.price === undefined) continue;
    const side = HIGH_TYPES.has(p.type) ? 'top' : LOW_TYPES.has(p.type) ? 'bottom' : null;
    if (!side) continue;
    const key = `${side}|${p.at}`;
    if (taken.has(key)) continue;
    taken.add(key);
    out.push({
      type: side === 'top' ? 'topo' : 'fundo',
      label: side === 'top' ? 'topo' : 'fundo',
      price: round(p.price),
      at: p.at,
      field: side === 'top' ? 'high' : 'low',
      side,
      stage: 'interno',
      role: 'interno',
    });
  }
  return out.sort((a, b) => Date.parse(a.at) - Date.parse(b.at));
}
